import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, X, KeyRound, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const LANGS = [
  ["ja", "日本語"],
  ["pt-BR", "Português (BR)"],
  ["en", "English"],
  ["vi", "Tiếng Việt"],
  ["id", "Bahasa Indonesia"],
];

const EMPTY = {
  employee_no: "", name: "", name_kana: "", display_name: "", tel: "", email: "",
  department: "", position: "", employment_type: "正社員", partner_id: "",
  start_date: "", end_date: "", language: "ja",
  can_upload_photos: true, can_view_photos: true, can_edit_photos: false, can_request_delete: false,
  notes: "", is_active: true,
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminStaff() {
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);

  const load = () => {
    api.get("/admin/staff").then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
    api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/staff/${editing.id}`, form);
        toast.success("保存しました");
      } else {
        const r = await api.post("/admin/staff", form);
        setIssued({ login_id: r.data.login_id, temp_password: r.data.temp_password, name: r.data.name });
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (s) => {
    if (!window.confirm(`${s.name} のパスワードを再発行しますか？`)) return;
    try {
      const r = await api.post(`/admin/staff/${s.id}/reset-password`);
      setIssued({ login_id: s.login_id, temp_password: r.data.temp_password, name: s.name });
    } catch {
      toast.error("再発行に失敗しました");
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/staff/${s.id}`, { ...s, is_active: !s.is_active });
      toast.success(s.is_active ? "無効にしました（ログインできなくなります）" : "有効にしました");
      load();
    } catch {
      toast.error("変更に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const copyIssued = () => {
    navigator.clipboard
      .writeText(`ログインID: ${issued.login_id}\n仮パスワード: ${issued.temp_password}\nログイン画面: ${window.location.origin}/staff/login`)
      .then(() => toast.success("コピーしました"));
  };

  return (
    <div data-testid="admin-staff-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">現場スタッフの管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">FIELD STAFF</p>
        </div>
        <button data-testid="staff-add-button" onClick={() => { setForm(EMPTY); setEditing({}); }} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
          <Plus className="h-4 w-4" />
          スタッフを登録
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">氏名</th>
              <th className="px-4 py-3">ログインID</th>
              <th className="px-4 py-3">言語</th>
              <th className="px-4 py-3">最終ログイン</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 text-xs font-bold text-white">
                  {s.name}
                  <span className="ml-2 font-normal text-gray-500">{s.employment_type}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{s.login_id}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{(LANGS.find(([v]) => v === s.language) || [])[1] || s.language}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{s.last_login_at ? new Date(s.last_login_at).toLocaleString("ja-JP") : "未ログイン"}</td>
                <td className="px-4 py-3 text-xs">{s.is_active ? <span className="text-green-400">有効</span> : <span className="text-red-400">無効</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`staff-edit-${s.id}`} onClick={() => { setForm({ ...EMPTY, ...s }); setEditing(s); }} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="編集">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`staff-reset-${s.id}`} onClick={() => resetPassword(s)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="パスワード再発行">
                      <KeyRound className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`staff-toggle-${s.id}`} onClick={() => toggleActive(s)} className={`border px-2.5 py-2 font-mono text-[10px] transition-colors ${s.is_active ? "border-line text-gray-400 hover:border-red-500 hover:text-red-400" : "border-line text-gray-400 hover:border-green-500 hover:text-green-400"}`}>
                      {s.is_active ? "無効化" : "有効化"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">スタッフが登録されていません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {issued && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <div data-testid="staff-issued-modal" className="w-full max-w-md border border-line bg-coal p-8">
            <h2 className="font-mincho text-lg font-bold text-white">{issued.name} さんのログイン情報</h2>
            <p className="mt-2 text-xs leading-relaxed text-gray-400">この画面は今だけ表示されます。スタッフに安全な方法で伝えてください。</p>
            <div className="mt-5 space-y-3 border border-line bg-ink p-4 font-mono text-sm">
              <p className="text-gray-400">ログインID: <span data-testid="issued-login-id" className="text-accent">{issued.login_id}</span></p>
              <p className="text-gray-400">仮パスワード: <span data-testid="issued-password" className="text-accent">{issued.temp_password}</span></p>
              <p className="text-gray-400">ログイン画面: <span className="text-white">/staff/login</span></p>
            </div>
            <div className="mt-5 flex gap-2">
              <button data-testid="issued-copy-button" onClick={copyIssued} className="flex flex-1 items-center justify-center gap-2 border border-accent py-3 text-sm font-bold text-accent transition-colors hover:bg-accent hover:text-ink">
                <Copy className="h-4 w-4" />
                コピー
              </button>
              <button data-testid="issued-close-button" onClick={() => setIssued(null)} className="flex-1 bg-accent py-3 text-sm font-bold text-ink transition-colors hover:bg-accent-hover">
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="staff-form" onSubmit={save} className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "スタッフを編集" : "スタッフを登録"}</h2>
              <button type="button" data-testid="staff-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="氏名 *"><input data-testid="staff-form-name" required value={form.name} onChange={set("name")} className={inputCls} /></Field>
              <Field label="フリガナ"><input value={form.name_kana} onChange={set("name_kana")} className={inputCls} /></Field>
              <Field label="表示名"><input value={form.display_name} onChange={set("display_name")} className={inputCls} /></Field>
              <Field label="社員番号（ログインIDになります）"><input data-testid="staff-form-employee-no" value={form.employee_no} onChange={set("employee_no")} className={inputCls} placeholder="例: N001" /></Field>
              <Field label="電話番号"><input value={form.tel} onChange={set("tel")} className={inputCls} /></Field>
              <Field label="メールアドレス"><input value={form.email} onChange={set("email")} className={inputCls} /></Field>
              <Field label="所属部署"><input value={form.department} onChange={set("department")} className={inputCls} /></Field>
              <Field label="役職"><input value={form.position} onChange={set("position")} className={inputCls} /></Field>
              <Field label="雇用区分">
                <select value={form.employment_type} onChange={set("employment_type")} className={inputCls}>
                  <option>正社員</option><option>パート</option><option>協力会社</option><option>派遣</option>
                </select>
              </Field>
              <Field label="所属協力会社（該当する場合）">
                <select data-testid="staff-form-partner" value={form.partner_id} onChange={set("partner_id")} className={inputCls}>
                  <option value="">—</option>
                  {partners.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </Field>
              <Field label="入社日・利用開始日"><input value={form.start_date} onChange={set("start_date")} className={inputCls} placeholder="2026-01-01" /></Field>
              <Field label="退職日・利用終了日"><input value={form.end_date} onChange={set("end_date")} className={inputCls} /></Field>
              <Field label="画面の言語">
                <select data-testid="staff-form-language" value={form.language} onChange={set("language")} className={inputCls}>
                  {LANGS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
                </select>
              </Field>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">写真の権限</span>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                {[["can_upload_photos", "登録"], ["can_view_photos", "閲覧"], ["can_edit_photos", "編集"], ["can_request_delete", "削除申請"]].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-gray-300">
                    <input data-testid={`staff-form-${key}`} type="checkbox" checked={form[key]} onChange={set(key)} className="h-4 w-4 accent-accent" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <Field label="備考"><textarea rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input data-testid="staff-form-active" type="checkbox" checked={form.is_active} onChange={set("is_active")} className="h-4 w-4 accent-accent" />
              有効（ログイン可能）
            </label>
            <button data-testid="staff-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : editing.id ? "保存する" : "登録してログイン情報を発行"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
